import { Controller } from "@hotwired/stimulus"
import { each } from "lodash"
import { parseNumber, formatNumber, forceSetValueOnSelect, resolveProductPrices } from "./../helpers"

export default class extends Controller {
  static targets = ["productId", "positionMore", "buttonLess", "buttonMore", "decidesEmpty", "taxField", "newPosition"]
  static outlets = ["invoice", "invoice--position", "dropdown-invoice-product"]

  static values = {
    positionsLimit: { type: Number, default: 10 },
    positionId: Number,
    positionIndex: String,
    positionKind: String,
    npTaxKinds: Array,
    fieldPrefix: { type: String, default: "" },
    reverseCharge: Boolean,
  }

  get departments() {
    return this.invoiceOutlet.departmentsValue
  }

  get priceSetup() {
    const base = this.invoiceOutlet.priceSetupValue
    return this.fieldPrefixValue ? { ...base, field_prefix: this.fieldPrefixValue } : base
  }

  connect() {
    if (this.reverseChargeValue && this.hasTaxFieldTarget) {
      const currentValue = this.taxFieldTarget.value // przed wyczyszczeniem opcji, inaczej znika (np. "np" dla zagranicznego nabywcy)
      if (this.taxFieldTarget.matches("select")) this.taxFieldTarget.replaceChildren()
      forceSetValueOnSelect(this.taxFieldTarget, currentValue)
    }
  }

  replaceComma(event) {
    const localeReplacements = {
      pl: { from: ".", to: "," },
      default: { from: ",", to: "." }
    }

    const { from, to } = localeReplacements[this.priceSetup.locale] || localeReplacements["default"]
    event.target.value = event.target.value.replaceAll(from, to)
  }

  /**
   * @param {{ params: { kind: string, quantity?: "to_unit"|"to_total" } }} event
   */
  calculateSum(event) {
    const id = this.positionIndexValue
    let toQuantity = event.params.quantity || "to_total"
    const fromKind = event.params.kind
    const options = {
      field_prefix: this.priceSetup.field_prefix,
      calculating_strategy: {
        position: "default",
        sum: "sum",
        invoice_form_price_kind: "net"
      },
      ignore_tax: false,
    }

    const preprefix = options.field_prefix + id
    const prefix = preprefix + (options.postfix || "")
    const fullPrefix = prefix + (options.price_prefix || "")

    const q = parseNumber(document.querySelector(prefix + "quantity").value, this.priceSetup)
    // zeby nie dzielic przez zero
    if (toQuantity === "to_unit" && q === 0) {
      toQuantity = "to_total"
    }

    const fromQ = toQuantity === "to_unit" ? "total_" : ""

    const fromPriceSelector = fullPrefix + fromQ + "price_" + fromKind
    const fromPriceElem = document.querySelector(fromPriceSelector)
    // szczegolny przypadek - zerujemy, bo zaciagna sie ceny (zakupu) z FIFO
    if (options.set_to_blank_if_blank && fromPriceElem.value === "") {
      ["price_net", "price_gross", "total_price_net", "total_price_gross"].forEach(function (priceKind) {
        document.querySelector(fullPrefix + priceKind).value = ""
      })
      return
    }

    const fromPrice = parseNumber(fromPriceElem.value, this.priceSetup)
    let tax = this.get(fullPrefix + 'tax'); if (isNaN(tax)) { tax = 0 }
    let tax2 = this.get(fullPrefix + 'tax2'); if (isNaN(tax2)) { tax2 = 0 }

    if (this.priceSetup.ignore_tax === true) {
      tax = tax2 = 0
    }

    // obliczamy ceny
    const prices = this.calculatePrices(fromPrice, q, fromKind, fromQ, tax, tax2)
    each(prices, (key, val) => {
      const opts = { ...this.priceSetup }
      if (val.toString().indexOf("total") > -1) {
        opts.app_digits = 2
      }

      this.set(fullPrefix + val, key, opts)
    })

    if (this.hasInvoiceOutlet && this.invoiceOutlet.hasDiscountTarget && this.invoiceOutlet.discountTarget.checked) {
      if (this.invoiceOutlet.discountKindValue !== "amount") {
        const discount = this.get(fullPrefix + "discount_percent") / 100
        this.set(fullPrefix + "discount", prices.total_price_gross * discount, { ...this.priceSetup, app_digits: 2 })
      } // TODO korekta
    }
    if (this.hasInvoicePositionOutlet && this.invoicePositionOutlet.hasSummaryNetTarget) { // TODO na razie tylko wersja desktop TW
      this.invoicePositionOutlet.calculateTotalSum()
    }
  }

  get(selector) {
    const element = document.querySelector(selector)
    if (element) {
      return parseNumber(element.value, this.priceSetup)
    }
    return NaN
  }

  set(selector, value, opts) {
    const element = document.querySelector(selector)
    if (element) {
      element.value = formatNumber(value, opts)
    }
    return NaN
  }

  calculatePrices(price, quantity, fromKind, fromQ, tax, tax2) {
    const oppositeKind = { net: 'gross', gross: 'net' }
    const oppositeQ = { total_: '', '': 'total_' }

    const kindConverter = {
      gross: function (priceGross, tax, tax2) { return priceGross / (1 + (tax / 100) + (tax2 / 100)) }, // net_from_gross
      net: function (priceNet, tax, tax2) { return priceNet * (1 + (tax / 100) + (tax2 / 100)) } // gross_from_net
    }

    const qConverter = {
      total_: function (price, q) { return price / q }, // unit_from_total
      "": function (price, q) { return (price * q).toFixed(2) * 1 } // total_from_unit // podobno fix na jakis blad
    }

    tax2 = tax2 || 0
    const fromKindTargetQuantity = qConverter[fromQ](price, quantity)

    const ret = {}
    ret[fromQ + 'price_' + fromKind] = price
    ret[fromQ + 'price_' + oppositeKind[fromKind]] = kindConverter[fromKind](price, tax, tax2)
    ret[oppositeQ[fromQ] + 'price_' + fromKind] = fromKindTargetQuantity // q_converter[from_q](price, quantity)
    ret[oppositeQ[fromQ] + 'price_' + oppositeKind[fromKind]] = kindConverter[fromKind](fromKindTargetQuantity, tax, tax2)

    return ret
  }

  // Otwiera shared modal wyszukiwania produktu dla tego wiersza.
  // Pobiera kontekst (warehouse, price_list) z invoiceOutlet i przekazuje do dropdownu.
  openProductSearch(event) {
    if (!this.hasDropdownInvoiceProductOutlet) return
    const params = {}
    if (this.hasInvoiceOutlet) {
      if (this.invoiceOutlet.warehouseIdValue !== 0) {
        params.warehouse_id = this.invoiceOutlet.warehouseIdValue
      }
      if (this.invoiceOutlet.hasPriceListTarget) {
        params.price_list_id = this.invoiceOutlet.priceListTarget.value
      }
    }
    this.dropdownInvoiceProductOutlet.openWithParams(event.currentTarget, params)
  }

  // Obsługuje wybranie produktu z shared modal.
  // Wywołanie: event "dropdown-invoice-product:selected" bąbelkujący od opener (button) przez ten element.
  productSelected(event) {
    const { product: rawProduct } = event.detail
    const product = this.hasInvoiceOutlet ? resolveProductPrices(rawProduct, this.invoiceOutlet.incomeValue) : rawProduct
    const prefix = `${this.priceSetup.field_prefix}${this.positionIndexValue}`

    let showMore = false

    const nameEl = this.element.querySelector("[id$='_name']:not([type='hidden'])")
    if (nameEl) nameEl.value = product.value ?? ""

    // product_id — mamy target
    if (this.hasProductIdTarget) this.productIdTarget.value = product.id ?? ""

    // Ilość i jednostka
    const qEl = document.querySelector(prefix + "quantity")
    if (qEl && product.quantity) qEl.value = product.quantity

    const unitEl = document.querySelector(prefix + "quantity_unit")
    if (unitEl) {
      if (product.quantity_unit) {
        forceSetValueOnSelect(unitEl, product.quantity_unit)
      } else {
        unitEl.value = ""
      }
      unitEl.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const priceNetEl = document.querySelector(prefix + "price_net")
    const priceGrossEl = document.querySelector(prefix + "price_gross")
    const totalPriceGrossEl = document.querySelector(prefix + "total_price_gross")
    const useGross = this.invoiceOutlet.priceSetupValue.price_kind === "gross"
    const preferredEl = useGross ? priceGrossEl : priceNetEl
    const el = preferredEl || totalPriceGrossEl

    let triggerEl = null
    if (el) {
      el.value = useGross ? product.price_gross : product.price_net
      triggerEl = el
    }

    let taxEl = null
    if (this.hasInvoiceOutlet && this.invoiceOutlet.taxVisibleValue && !["wdt", "export_products"].includes(this.invoiceOutlet.kindValue)) {
      if (this.invoiceOutlet.autocompleteTax) {
        // na dokumentach z wymuszoną stawką (RR: ryczałt) stawka z karty produktu jej nie nadpisuje
        const forcedTax = this.invoiceOutlet.forcedPositionTaxValue
        if (!forcedTax) this.invoiceOutlet.setDefaultTax()
        const itemTax = forcedTax || this.invoiceOutlet.defaultTax || product.tax
        taxEl = document.querySelector(prefix + "tax")
        const mossOn = this.invoiceOutlet.hasMossOutlet && this.invoiceOutlet.mossOutlet.useMossTarget.checked
        const hasMatchingOption = !taxEl?.matches("select") || Array.from(taxEl.options).some((option) => option.value === itemTax)
        const hasOwnTax = itemTax !== undefined && itemTax !== null && itemTax !== ""
        if (hasOwnTax && (!mossOn || hasMatchingOption)) forceSetValueOnSelect(taxEl, itemTax)
      }
    } else if (this.hasInvoiceOutlet && !this.invoiceOutlet.taxVisibleValue && this.invoiceOutlet.accountCountryValue === "fr") {
      taxEl = document.querySelector(prefix + "tax")
      forceSetValueOnSelect(taxEl, "disabled")
    }

    if (product.description?.length > 0 && document.querySelector(prefix + "description")) {
      document.querySelector(prefix + "description").value = product.description
      showMore = true
    }

    if (product.code?.length > 0 && document.querySelector(prefix + "code")) {
      document.querySelector(prefix + "code").value = product.code
      showMore = true
    }

    if (product.gtu_codes?.length > 0 && document.querySelector(prefix + "gtu_code")?.length > 0) {
      document.querySelector(prefix + "gtu_code").value = product.gtu_codes[0]
      showMore = true
    }

    if (product.additional_info?.length > 0 && document.querySelector(prefix + "additional_info")) {
      document.querySelector(prefix + "additional_info").value = product.additional_info
      showMore = true
    }

    if (showMore && document.querySelector("[data-toggle-position-button]")) {
      document.querySelector("[data-toggle-position-button]").dispatchEvent(new Event("click"))
    }

    if (triggerEl) triggerEl.dispatchEvent(new Event("change"))
    if (taxEl) taxEl.dispatchEvent(new Event("change"))

    if (this.hasInvoiceOutlet && this.invoiceOutlet.handleCondNoteValue) {
      this.invoiceOutlet.updateCondNote()
    }
  }

  changeProduct() {
    if (this.hasProductIdTarget && this.productIdTarget.value.length > 0) {
      this.productIdTarget.value = ''
    }
  }

  toggleAdditional() {
    this.positionMoreTargets.forEach((target) => target.classList.toggle("hidden"))
    if (this.buttonLessTarget.classList.contains("hidden")) {
      this.buttonMoreTarget.classList.add("hidden")
      this.buttonLessTarget.classList.remove("hidden")
    } else {
      this.buttonLessTarget.classList.add("hidden")
      this.buttonMoreTarget.classList.remove("hidden")
    }
  }

  isEmpty() {
    return !this.decidesEmptyTargets.some(e => e.value !== "")
  }

  /**
   * @param {HTMLOptionElement[]} options
   * @returns {boolean} true jeśli wszystkie stawki pasują do nowych opcji (lub pozycje są puste); false jeśli wymagane powiadomienie o zmianie stawki
   */
  setTaxOptions(options) {
    const infix = this.invoiceOutlet.correctionValue ? "[correction_after_attributes]" : ""
    const row = this.element
    let allTaxesMatched = true
    row.querySelectorAll(`select[name^="invoice[positions_attributes]"][name$='${infix}[tax]']`).forEach((/** @type {HTMLSelectElement} */ taxField) => {
      const tmpValue = taxField.value
      taxField.replaceChildren(...options.map((option) => option.cloneNode(true)))
      taxField.value = tmpValue
      if (taxField.value !== tmpValue) {
        const isNpTax = this.npTaxKindsValue.includes(tmpValue.toLowerCase())
        const grossEl = row.querySelector(`[name^="invoice[positions_attributes]"][name$='${infix}[total_price_gross]']`)
        const netEl = row.querySelector(`[name^="invoice[positions_attributes]"][name$='${infix}[total_price_net]']`)
        const emptyPrices = !grossEl || !netEl || grossEl.value === "" || netEl.value === ""
        if (emptyPrices || isNpTax) {
          // puste ceny → cicha zamiana (pozycja niewypełniona, nie ma co notyfikować);
          // NP z cenami → zamiana + powiadomienie (użytkownik musi wiedzieć, że stawka się zmieniła)
          taxField.value = taxField.options[0].value
          if (isNpTax && !emptyPrices) allTaxesMatched = false
        } else {
          // już coś było wpisane wcześniej więc trzeba dodać nową opcję
          const newOption = document.createElement("option")
          newOption.value = tmpValue
          newOption.innerText = tmpValue
          taxField.appendChild(newOption)
          taxField.value = tmpValue
          allTaxesMatched = false
        }
        // zapamiętanie poprzedniej stawki do przywrócenia po wyłączeniu OSS (nie nadpisuj jeśli już zapamiętano)
        if (!taxField.dataset.preMossTax) { taxField.dataset.preMossTax = tmpValue }
      }
    })
    return allTaxesMatched
  }

  restorePreMossTaxes() {
    const infix = this.invoiceOutlet.correctionValue ? "[correction_after_attributes]" : ""
    this.element.querySelectorAll(`select[name$='${infix}[tax]']`).forEach((taxField) => {
      if (taxField.dataset.preMossTax) {
        const savedTax = taxField.dataset.preMossTax
        taxField.value = savedTax
        delete taxField.dataset.preMossTax
        if (taxField.value === savedTax) {
          taxField.dispatchEvent(new Event("change", { bubbles: true }))
        } else {
          console.warn(`[position_calculator] Nie udało się przywrócić stawki "${savedTax}"`)
        }
      }
    })
  }

  subtotalWithQuantityAlert() {
    let lastQuantity = null
    let different = false

    document.querySelectorAll("[id^=detail_]").forEach((element) => {
      if (window.getComputedStyle(element).display === "none") {
        return
      }

      const currentKind = element.querySelector("[id$=_kind]").value
      if (currentKind === "text_separator" || currentKind === "page_break") {
        return
      }

      if (currentKind === "subtotal_with_quantity") {
        const name = element.querySelector("[id$=_name]")
        name.parentNode.insertAdjacentHTML('beforeend', this.subtotalAlertTemplate())
        element.querySelector(".subtotal_alert").classList.toggle("hidden", !different)

        lastQuantity = null
        different = false
      } else if (currentKind === "subtotal" || currentKind === "subtotal_with_weight") {
        lastQuantity = null
        different = false
      } else {
        const currentQuantity = (element.querySelector("[id$=_quantity_unit]").value || "")

        if (lastQuantity === null) {
          lastQuantity = currentQuantity
        } else if (lastQuantity !== currentQuantity) {
          different = true
        }
      }
    })
  }
}
